// components/admin/EnquiryManager.js - Complete with Multiple Image Upload & Gallery
import React, { useState, useEffect, useMemo } from 'react';
import { dbOnValue, dbUpdate, dbPush, dbRemove, cleanFirebaseData, dbSet } from '../../config/firebase';
import { uploadImage, deleteImage } from '../../config/appwrite';
import { 
  Search, Eye, CheckCircle, UserPlus, Clock, Calendar,
  Phone, Mail, MessageSquare, X, Loader, Plus, Image as ImageIcon,
  Trash2, Edit2, Upload, Download, TrendingUp,
  Users, Award, Briefcase, Activity,
  Filter, DollarSign, FileText, AlertCircle, Tag,
  Folder, FolderPlus, ChevronRight, ChevronDown, Grid, List,
  Camera, Image, Copy, Check
} from 'lucide-react';
import { toast } from 'react-toastify';

// Gallery Image Card Component
const GalleryImageCard = ({ image, onDelete, onEdit, isMobile }) => (
  <div style={{
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    aspectRatio: '4/3',
    background: '#f8fafc',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s, boxShadow 0.2s',
    cursor: 'pointer'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
  }}>
    <img src={image.url} alt={image.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
      padding: '12px 8px 8px',
      color: 'white'
    }}>
      <p style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>{image.title}</p>
      {image.description && <p style={{ fontSize: '10px', opacity: 0.8 }}>{image.description}</p>}
    </div>
    <div style={{
      position: 'absolute',
      top: '8px',
      right: '8px',
      display: 'flex',
      gap: '6px',
      zIndex: 10
    }}>
      <button onClick={(e) => { e.stopPropagation(); onEdit(image); }} style={{
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: 'white',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Edit2 size={14} color="#64748b" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(image); }} style={{
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: 'white',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Trash2 size={14} color="#ef4444" />
      </button>
    </div>
  </div>
);

// Add/Edit Gallery Category Modal
const GalleryCategoryModal = ({ isOpen, onClose, onSave, editingCategory }) => {
  const [name, setName] = useState(editingCategory?.name || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Category name required');
      return;
    }
    setLoading(true);
    await onSave({ id: editingCategory?.id, name: name.trim() });
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', zIndex: 1101, width: '90%', maxWidth: '400px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a0a0a' }}>
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h3>
          <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
        <input 
          type="text" 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          placeholder="Category name (e.g., Wedding, Birthday)" 
          style={{ width: '100%', padding: '12px', marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }} 
          autoFocus 
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading} style={{ flex: 1, padding: '10px', background: loading ? '#94a3b8' : '#dc2626', color: 'white', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </>
  );
};

// Add/Edit Gallery Image Modal with Multiple Image Upload
const GalleryImageModal = ({ isOpen, onClose, onSave, editingImage, categories }) => {
  const [title, setTitle] = useState(editingImage?.title || '');
  const [description, setDescription] = useState(editingImage?.description || '');
  const [categoryId, setCategoryId] = useState(editingImage?.categoryId || categories[0]?.id || '');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState(editingImage?.images || []);
  const [link, setLink] = useState(editingImage?.link || '');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 10 images per upload
    const maxImages = 10;
    const filesToAdd = files.slice(0, maxImages - imageFiles.length);
    
    if (filesToAdd.length === 0) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }
    
    filesToAdd.forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
        setImageFiles(prev => [...prev, file]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (index, imageId) => {
    if (window.confirm('Remove this image?')) {
      try {
        if (imageId) {
          await deleteImage(imageId);
        }
        setExistingImages(prev => prev.filter((_, i) => i !== index));
        toast.success('Image removed');
      } catch (error) {
        toast.error('Failed to remove image');
      }
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!categoryId) {
      toast.error('Please select a category');
      return;
    }
    if (imageFiles.length === 0 && existingImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    
    try {
      // Upload new images
      const uploadedImages = [];
      let completed = 0;
      
      for (const file of imageFiles) {
        const uploadResult = await uploadImage(file);
        uploadedImages.push({
          url: uploadResult.url,
          imageId: uploadResult.id,
          title: title,
          description: description
        });
        completed++;
        setUploadProgress(Math.round((completed / imageFiles.length) * 100));
      }
      
      // Combine existing and new images
      const allImages = [...existingImages, ...uploadedImages];
      
      const imageData = {
        title: title.trim(),
        description: description.trim() || '',
        categoryId: categoryId,
        images: allImages,
        link: link.trim() || `/category/${categoryId}`,
        updatedAt: new Date().toISOString(),
        ...(editingImage?.createdAt && { createdAt: editingImage.createdAt })
      };

      const cleanData = cleanFirebaseData(imageData);
      await onSave(cleanData, editingImage?.id);
      onClose();
    } catch (error) {
      toast.error('Failed to save image: ' + error.message);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', zIndex: 1101, width: '90%', maxWidth: '600px', maxHeight: '85vh', overflow: 'auto', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700 }}>{editingImage ? 'Edit Gallery Item' : 'Add Gallery Item'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>✕</button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Category *</label>
          <select 
            value={categoryId} 
            onChange={(e) => setCategoryId(e.target.value)} 
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }}
          >
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Birthday Decor"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the decoration theme"
            rows="2"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Link (Optional)</label>
          <input
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="/category/birthday"
            style={inputStyle}
          />
        </div>

        {/* Existing Images */}
        {existingImages.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Existing Images</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {existingImages.map((img, idx) => (
                <div key={idx} style={{ position: 'relative', width: '80px', height: '80px' }}>
                  <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                  <button
                    onClick={() => removeExistingImage(idx, img.imageId)}
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Images Upload */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Upload Images *</label>
          <div
            onClick={() => document.getElementById('galleryImages').click()}
            style={{
              border: '2px dashed #cbd5e1',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: '#fafbfc'
            }}
          >
            <input id="galleryImages" type="file" multiple accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
            <Camera size={32} color="#dc2626" style={{ marginBottom: '8px' }} />
            <p style={{ color: '#334155', fontSize: '13px' }}>Click to upload multiple images</p>
            <p style={{ color: '#94a3b8', fontSize: '11px' }}>JPG, PNG up to 5MB each (Max 10 images)</p>
          </div>
          
          {/* Image Previews */}
          {imagePreviews.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <label style={labelStyle}>New Images ({imagePreviews.length})</label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {imagePreviews.map((preview, idx) => (
                  <div key={idx} style={{ position: 'relative', width: '80px', height: '80px' }}>
                    <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                    <button
                      onClick={() => removeImage(idx)}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#dc2626', transition: 'width 0.3s' }} />
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Uploading... {uploadProgress}%</p>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '10px', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading} style={{ flex: 1, padding: '10px', background: loading ? '#94a3b8' : '#dc2626', color: 'white', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {loading ? 'Saving...' : (editingImage ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </>
  );
};

// Enquiry Detail Modal (kept from previous version)
const EnquiryDetailModal = ({ enquiry, onClose, onAssign, staffList, onConfirmOrder, confirming }) => {
  const [assignedTo, setAssignedTo] = useState(enquiry?.assignedToId || '');
  const [assignLoading, setAssignLoading] = useState(false);

  if (!enquiry) return null;

  const statusConfig = {
    pending: { label: 'Pending', color: '#f59e0b', bg: '#fffbeb' },
    assigned: { label: 'Assigned', color: '#3b82f6', bg: '#eff6ff' },
    contacted: { label: 'Contacted', color: '#8b5cf6', bg: '#f5f3ff' },
    completed: { label: 'Completed', color: '#10b981', bg: '#ecfdf5' }
  };
  const currentStatus = statusConfig[enquiry.status] || statusConfig.pending;

  const handleAssign = async () => {
    if (!assignedTo) {
      toast.error('Please select a staff member');
      return;
    }
    setAssignLoading(true);
    try {
      await onAssign(enquiry.firebaseKey, assignedTo);
      toast.success(`Enquiry assigned successfully`);
    } catch (error) {
      toast.error('Assignment failed: ' + error.message);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleConfirmOrder = () => {
    if (onConfirmOrder) {
      onConfirmOrder(enquiry);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', zIndex: 1001, width: '90%', maxWidth: '700px', maxHeight: '85vh', overflow: 'auto', padding: '0' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 2 }}>
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1a0a0a', margin: 0 }}>Decoration Enquiry</h3>
            <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>
              ID: {enquiry.firebaseKey?.slice(0, 8)} | Submitted: {new Date(enquiry.createdAt).toLocaleString()}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600, background: currentStatus.bg, color: currentStatus.color }}>
              {currentStatus.label}
            </span>
            <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Customer Information */}
          <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}>
              <Users size={18} /> Customer Information
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Full Name</p><p style={{ fontSize: '0.9rem', fontWeight: 600 }}>{enquiry.name || '—'}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Phone</p><p style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>{enquiry.phone || '—'}{enquiry.phone && <a href={`tel:${enquiry.phone}`} style={{ padding: '4px 12px', background: '#10b981', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.7rem' }}>Call</a>}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Email</p><p style={{ fontSize: '0.85rem' }}>{enquiry.email || '—'}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Submitted</p><p style={{ fontSize: '0.85rem' }}>{new Date(enquiry.createdAt).toLocaleString()}</p></div>
            </div>
          </div>

          {/* Event Details */}
          <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}>
              <Calendar size={18} /> Event Details
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Event Type</p><p style={{ fontSize: '0.9rem', textTransform: 'capitalize' }}>{enquiry.eventType || '—'}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Event Date</p><p style={{ fontSize: '0.85rem' }}>{enquiry.eventDate ? new Date(enquiry.eventDate).toLocaleDateString() : 'Not specified'}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Venue</p><p style={{ fontSize: '0.85rem' }}>{enquiry.eventVenue || 'Not specified'}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Guest Count</p><p style={{ fontSize: '0.85rem' }}>{enquiry.guestCount ? `${enquiry.guestCount} guests` : 'Not specified'}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Budget</p><p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#10b981' }}>{enquiry.budget ? `₹${parseInt(enquiry.budget).toLocaleString()}` : 'Not specified'}</p></div>
              <div><p style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px' }}>Theme</p><p style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>{enquiry.theme || 'Not specified'}</p></div>
            </div>
          </div>

          {/* Requirements */}
          <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}>
              <FileText size={18} /> Requirements
            </h4>
            <p style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{enquiry.requirements || 'No additional requirements specified.'}</p>
          </div>

          {/* Assignment Section */}
          <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}>
              <Briefcase size={18} /> Assignment
            </h4>
            {enquiry.assignedTo && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#e3f2fd', borderRadius: '12px' }}>
                <p style={{ fontSize: '0.7rem', color: '#1565c0', marginBottom: '4px' }}>Currently Assigned To:</p>
                <p style={{ fontSize: '0.9rem', fontWeight: 600 }}>{enquiry.assignedTo}</p>
              </div>
            )}
            <div>
              <p style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '8px' }}>Assign to Staff Member:</p>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} style={{ flex: 2, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '0.85rem', background: 'white' }}>
                  <option value="">Select Staff Member</option>
                  {staffList?.map(s => (<option key={s.id} value={s.id}>{s.name} ({s.email})</option>))}
                </select>
                <button onClick={handleAssign} disabled={assignLoading || !assignedTo} style={{ flex: 1, padding: '10px 20px', background: assignLoading || !assignedTo ? '#94a3b8' : '#dc2626', color: 'white', borderRadius: '10px', cursor: assignLoading || !assignedTo ? 'not-allowed' : 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  {assignLoading ? <Loader size={16} className="spinner" /> : <UserPlus size={16} />}
                  {assignLoading ? 'Assigning...' : 'Assign Enquiry'}
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button onClick={onClose} style={{ flex: 1, padding: '12px', background: '#f1f5f9', borderRadius: '10px', cursor: 'pointer', fontWeight: 500, border: 'none' }}>Close</button>
            <a href={`tel:${enquiry.phone}`} style={{ flex: 1, padding: '12px', background: '#10b981', color: 'white', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, textAlign: 'center', textDecoration: 'none' }}>Call Customer</a>
            <button onClick={handleConfirmOrder} disabled={confirming} style={{ flex: 1.5, padding: '12px', background: confirming ? '#94a3b8' : '#dc2626', color: 'white', borderRadius: '10px', cursor: confirming ? 'not-allowed' : 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {confirming ? <Loader size={16} className="spinner" /> : <CheckCircle size={16} />}
              {confirming ? 'Confirming...' : 'Confirm Order'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Main EnquiryManager Component
const EnquiryManager = ({ staffList }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('enquiries');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [customDateFilter, setCustomDateFilter] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load enquiries
  useEffect(() => {
    const unsubscribe = dbOnValue('decoration_enquiries', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const enquiryList = Object.entries(data).map(([firebaseKey, enquiry]) => ({
          firebaseKey,
          id: enquiry.id || firebaseKey,
          ...enquiry,
          createdAt: enquiry.createdAt || new Date().toISOString()
        }));
        setEnquiries(enquiryList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setEnquiries([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Load gallery categories
  useEffect(() => {
    const unsubscribe = dbOnValue('decoration_gallery_categories', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const categoriesList = Object.entries(data).map(([key, cat]) => ({
          id: key,
          ...cat
        }));
        setGalleryCategories(categoriesList);
        if (categoriesList.length > 0 && !activeCategory) {
          setActiveCategory(categoriesList[0]);
        }
      } else {
        const defaultCategories = [
          { id: 'wedding', name: 'Wedding' },
          { id: 'birthday', name: 'Birthday' },
          { id: 'anniversary', name: 'Anniversary' },
          { id: 'corporate', name: 'Corporate' },
          { id: 'festival', name: 'Festival' }
        ];
        setGalleryCategories(defaultCategories);
        if (!activeCategory) setActiveCategory(defaultCategories[0]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load gallery items
  useEffect(() => {
    const unsubscribe = dbOnValue('decoration_gallery', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const itemsList = Object.entries(data).map(([key, item]) => ({
          id: key,
          ...item
        }));
        setGalleryItems(itemsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setGalleryItems([]);
      }
    });
    return () => unsubscribe();
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pending', color: '#f59e0b', bg: '#fffbeb', icon: Clock },
      assigned: { label: 'Assigned', color: '#3b82f6', bg: '#eff6ff', icon: UserPlus },
      contacted: { label: 'Contacted', color: '#8b5cf6', bg: '#f5f3ff', icon: Phone },
      completed: { label: 'Completed', color: '#10b981', bg: '#ecfdf5', icon: CheckCircle }
    };
    return configs[status] || configs.pending;
  };

  // Category CRUD
  const saveCategories = async (newCats) => {
    setGalleryCategories(newCats);
    try {
      await dbSet('decoration_gallery_categories', newCats);
    } catch (error) {
      console.error('Error saving categories:', error);
    }
  };

  const handleAddCategory = async (cat) => {
    const newCat = { id: `gallery_cat_${Date.now()}`, name: cat.name, createdAt: new Date().toISOString() };
    await saveCategories([...galleryCategories, newCat]);
    toast.success('Category added');
    setShowCategoryModal(false);
    setEditingCategory(null);
  };

  const handleEditCategory = async (cat) => {
    const updated = galleryCategories.map(c => c.id === cat.id ? { ...c, name: cat.name } : c);
    await saveCategories(updated);
    if (activeCategory?.id === cat.id) setActiveCategory(updated.find(c => c.id === cat.id));
    toast.success('Category updated');
    setShowCategoryModal(false);
    setEditingCategory(null);
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" and all items in this category?`)) return;
    const itemsInCategory = galleryItems.filter(item => item.categoryId === cat.id);
    for (const item of itemsInCategory) {
      if (item.images) {
        for (const img of item.images) {
          if (img.imageId) await deleteImage(img.imageId);
        }
      }
      await dbRemove(`decoration_gallery/${item.id}`);
    }
    const newCats = galleryCategories.filter(c => c.id !== cat.id);
    await saveCategories(newCats);
    if (activeCategory?.id === cat.id) setActiveCategory(newCats[0] || null);
    toast.success('Category deleted');
  };

  // Gallery item operations
  const handleAddGalleryItem = async (itemData, itemId = null) => {
    try {
      if (itemId) {
        await dbUpdate(`decoration_gallery/${itemId}`, itemData);
        toast.success('Gallery item updated successfully!');
      } else {
        await dbPush('decoration_gallery', { ...itemData, createdAt: new Date().toISOString() });
        toast.success('Gallery item added successfully!');
      }
      setShowGalleryModal(false);
      setEditingImage(null);
    } catch (error) {
      toast.error('Failed to save gallery item: ' + error.message);
    }
  };

  const handleDeleteGalleryItem = async (item) => {
    if (window.confirm(`Delete "${item.title}" from gallery?`)) {
      try {
        if (item.images) {
          for (const img of item.images) {
            if (img.imageId) await deleteImage(img.imageId);
          }
        }
        await dbRemove(`decoration_gallery/${item.id}`);
        toast.success('Gallery item deleted');
      } catch (error) {
        toast.error('Failed to delete item');
      }
    }
  };

  // Assign to staff
  const assignToStaff = async (firebaseKey, staffId) => {
    const staff = staffList?.find(s => s.id === staffId);
    if (!staff) {
      toast.error('Staff member not found');
      return;
    }
    
    try {
      await dbUpdate(`decoration_enquiries/${firebaseKey}`, {
        assignedTo: staff.name,
        assignedToId: staffId,
        assignedToEmail: staff.email,
        assignedAt: new Date().toISOString(),
        status: 'assigned',
        updatedAt: new Date().toISOString()
      });
      toast.success(`Enquiry assigned to ${staff.name}`);
      setEnquiries(prev => prev.map(e => 
        e.firebaseKey === firebaseKey 
          ? { ...e, assignedTo: staff.name, assignedToId: staffId, status: 'assigned' }
          : e
      ));
    } catch (error) {
      toast.error('Assignment failed: ' + error.message);
      throw error;
    }
  };

  // Confirm Order
  const confirmOrder = async (enquiry) => {
    setConfirmingOrder(true);
    try {
      await dbUpdate(`decoration_enquiries/${enquiry.firebaseKey}`, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      toast.success(`Order confirmed for ${enquiry.name}`);
      setEnquiries(prev => prev.map(e => 
        e.firebaseKey === enquiry.firebaseKey 
          ? { ...e, status: 'completed' }
          : e
      ));
      setShowDetailModal(false);
      setSelectedEnquiry(null);
    } catch (error) {
      toast.error('Failed to confirm order: ' + error.message);
    } finally {
      setConfirmingOrder(false);
    }
  };

  // Get filtered items for active category
  const filteredGalleryItems = useMemo(() => {
    if (!activeCategory) return [];
    return galleryItems.filter(item => item.categoryId === activeCategory.id);
  }, [galleryItems, activeCategory]);

  // Get preview image for category (first image of first item)
  const getCategoryPreview = (categoryId) => {
    const categoryItems = galleryItems.filter(item => item.categoryId === categoryId);
    if (categoryItems.length > 0 && categoryItems[0].images && categoryItems[0].images.length > 0) {
      return categoryItems[0].images[0].url;
    }
    return null;
  };

  // Filter enquiries
  const filteredEnquiries = useMemo(() => {
    let result = [...enquiries];
    
    if (filter !== 'all') {
      result = result.filter(e => e.status === filter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(e => 
        e.name?.toLowerCase().includes(term) ||
        e.email?.toLowerCase().includes(term) ||
        e.phone?.includes(term) ||
        e.eventType?.toLowerCase().includes(term)
      );
    }
    
    if (customDateFilter && dateRange.start) {
      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      const endDate = dateRange.end ? new Date(dateRange.end) : new Date();
      if (dateRange.end) endDate.setHours(23, 59, 59, 999);
      
      result = result.filter(e => {
        const created = new Date(e.createdAt);
        return created >= startDate && created <= endDate;
      });
    }
    
    return result;
  }, [enquiries, filter, searchTerm, customDateFilter, dateRange]);

  const statusCounts = {
    all: enquiries.length,
    pending: enquiries.filter(e => e.status === 'pending').length,
    assigned: enquiries.filter(e => e.status === 'assigned').length,
    contacted: enquiries.filter(e => e.status === 'contacted').length,
    completed: enquiries.filter(e => e.status === 'completed').length
  };

  const clearDateFilter = () => {
    setDateRange({ start: '', end: '' });
    setCustomDateFilter(false);
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Name', 'Email', 'Phone', 'Event Type', 'Event Date', 'Budget', 'Status', 'Assigned To', 'Submitted On'];
    const rows = filteredEnquiries.map(e => [
      e.firebaseKey?.slice(0, 8),
      e.name,
      e.email,
      e.phone,
      e.eventType,
      e.eventDate ? new Date(e.eventDate).toLocaleDateString() : '',
      e.budget || '',
      e.status,
      e.assignedTo || '',
      new Date(e.createdAt).toLocaleString()
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enquiries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loader size={40} className="spinner" style={{ color: '#dc2626' }} />
      </div>
    );
  }

  return (
    <div>
      {/* Simple Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
        gap: '16px', 
        marginBottom: '24px' 
      }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={20} color="#3b82f6" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>{enquiries.length}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Total Enquiries</div>
            </div>
          </div>
        </div>
        
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={20} color="#f59e0b" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>{statusCounts.pending}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Pending</div>
            </div>
          </div>
        </div>
        
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserPlus size={20} color="#8b5cf6" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>{statusCounts.assigned}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Assigned</div>
            </div>
          </div>
        </div>
        
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={20} color="#10b981" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a0a0a' }}>{statusCounts.completed}</div>
              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Completed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', flexWrap: 'wrap' }}>
        <button
          onClick={() => { setActiveTab('enquiries'); setFilter('all'); setCustomDateFilter(false); }}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: 'none',
            background: activeTab === 'enquiries' ? '#dc2626' : 'transparent',
            color: activeTab === 'enquiries' ? 'white' : '#64748b',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <MessageSquare size={16} /> Enquiries ({enquiries.length})
        </button>
        <button
          onClick={() => setActiveTab('gallery')}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: 'none',
            background: activeTab === 'gallery' ? '#dc2626' : 'transparent',
            color: activeTab === 'gallery' ? 'white' : '#64748b',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <ImageIcon size={16} /> Gallery ({galleryItems.length})
        </button>
      </div>

      {activeTab === 'enquiries' ? (
        <>
          {/* Status Filter Chips */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {Object.entries(statusCounts).map(([key, count]) => (
              <button
                key={key}
                onClick={() => { setFilter(key); setCustomDateFilter(false); }}
                style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  border: filter === key ? '2px solid #dc2626' : '1px solid #e2e8f0',
                  background: filter === key ? '#fff5f5' : 'white',
                  color: filter === key ? '#dc2626' : '#64748b',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {key} ({count})
              </button>
            ))}
          </div>

          {/* Search & Date Filter Bar */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, event..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 40px 10px 38px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    outline: 'none'
                  }}
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <X size={14} />
                  </button>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Calendar size={14} color="#64748b" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => { setDateRange({ ...dateRange, start: e.target.value }); setCustomDateFilter(true); }}
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.75rem' }}
                />
                <span>-</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => { setDateRange({ ...dateRange, end: e.target.value }); setCustomDateFilter(true); }}
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.75rem' }}
                />
                {customDateFilter && (
                  <button onClick={clearDateFilter} style={{ padding: '6px 12px', background: '#f1f5f9', borderRadius: '6px', fontSize: '0.7rem', cursor: 'pointer' }}>Clear</button>
                )}
              </div>
              
              <button onClick={exportToCSV} style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Download size={14} /> Export
              </button>
            </div>
          </div>

          {/* Enquiries Table */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', overflowX: 'auto' }}>
            {filteredEnquiries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
                <MessageSquare size={48} color="#cbd5e1" />
                <p style={{ marginTop: '12px' }}>No enquiries found</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Event</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Assigned To</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.map(enquiry => {
                    const statusConfig = getStatusConfig(enquiry.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr key={enquiry.firebaseKey} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={tdStyle}>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {new Date(enquiry.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{enquiry.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{enquiry.phone}</div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ textTransform: 'capitalize' }}>{enquiry.eventType || '—'}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600, background: statusConfig.bg, color: statusConfig.color, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <StatusIcon size={12} /> {statusConfig.label}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '12px', fontSize: '0.7rem' }}>
                            {enquiry.assignedTo || 'Unassigned'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <button
                            onClick={() => { setSelectedEnquiry(enquiry); setShowDetailModal(true); }}
                            style={{ padding: '6px 14px', background: '#f1f5f9', borderRadius: '6px', border: 'none', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: '#475569' }}
                          >
                            <Eye size={12} /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        // Gallery Tab with Categories and Multiple Images
        <div>
          {/* Header with Add Category Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px', color: '#1a0a0a' }}>Decoration Gallery</h3>
              <p style={{ color: '#64748b', fontSize: '13px' }}>Manage categories and gallery items with multiple images</p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 600
                }}
              >
                <FolderPlus size={16} /> New Category
              </button>
              <button
                onClick={() => { setEditingImage(null); setShowGalleryModal(true); }}
                style={{
                  padding: '10px 20px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 600
                }}
              >
                <Plus size={16} /> Add Gallery Item
              </button>
            </div>
          </div>

          {/* Categories Grid with Preview Images */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', 
            gap: '12px', 
            marginBottom: '24px' 
          }}>
            {galleryCategories.map(cat => {
              const itemCount = filteredGalleryItems.filter(item => item.categoryId === cat.id).length;
              const previewImage = getCategoryPreview(cat.id);
              const isActive = activeCategory?.id === cat.id;
              
              return (
                <div 
                  key={cat.id}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    cursor: 'pointer',
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: isActive ? '2px solid #dc2626' : '1px solid rgba(220,38,38,0.15)',
                    boxShadow: isActive ? '0 4px 12px rgba(220,38,38,0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Category Preview Image */}
                  <div style={{ 
                    position: 'relative',
                    aspectRatio: '16/9',
                    overflow: 'hidden',
                    background: '#f8fafc'
                  }}>
                    {previewImage ? (
                      <img 
                        src={previewImage} 
                        alt={cat.name}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          transition: 'transform 0.3s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #fff5f5, #fff)'
                      }}>
                        <ImageIcon size={32} color="#dc2626" opacity={0.5} />
                      </div>
                    )}
                    
                    {/* Active Indicator */}
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#dc2626',
                        boxShadow: '0 0 0 2px white'
                      }} />
                    )}
                  </div>
                  
                  {/* Category Info */}
                  <div style={{ padding: '12px', textAlign: 'center' }}>
                    <h3 style={{ 
                      fontSize: isMobile ? '13px' : '14px', 
                      fontWeight: 600, 
                      marginBottom: '4px',
                      color: isActive ? '#dc2626' : '#1a0a0a'
                    }}>
                      {cat.name}
                    </h3>
                    <p style={{ 
                      fontSize: '11px', 
                      color: '#64748b'
                    }}>
                      {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Gallery Items for Selected Category */}
          {activeCategory && (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #dc2626'
              }}>
                <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: 700, margin: 0, color: '#1a0a0a' }}>
                  {activeCategory.name} Gallery
                </h2>
                <span style={{ 
                  background: '#f1f5f9', 
                  padding: '4px 12px', 
                  borderRadius: '20px', 
                  fontSize: '12px', 
                  color: '#64748b' 
                }}>
                  {filteredGalleryItems.length} items
                </span>
              </div>

              {filteredGalleryItems.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0'
                }}>
                  <ImageIcon size={48} style={{ marginBottom: '16px', opacity: 0.5, color: '#dc2626' }} />
                  <h3 style={{ fontSize: '1rem', marginBottom: '8px', color: '#1a0a0a' }}>No gallery items in this category</h3>
                  <button
                    onClick={() => { setEditingImage(null); setShowGalleryModal(true); }}
                    style={{ marginTop: '16px', padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    + Add First Item
                  </button>
                </div>
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
                  gap: '20px' 
                }}>
                  {filteredGalleryItems.map(item => (
                    <GalleryImageCard
                      key={item.id}
                      image={item}
                      onDelete={handleDeleteGalleryItem}
                      onEdit={() => { setEditingImage(item); setShowGalleryModal(true); }}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showDetailModal && selectedEnquiry && (
        <EnquiryDetailModal
          enquiry={selectedEnquiry}
          onClose={() => { setShowDetailModal(false); setSelectedEnquiry(null); }}
          onAssign={assignToStaff}
          staffList={staffList || []}
          onConfirmOrder={confirmOrder}
          confirming={confirmingOrder}
        />
      )}

      {showCategoryModal && (
        <GalleryCategoryModal
          isOpen={showCategoryModal}
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
          onSave={editingCategory ? handleEditCategory : handleAddCategory}
          editingCategory={editingCategory}
        />
      )}

      {showGalleryModal && (
        <GalleryImageModal
          isOpen={showGalleryModal}
          onClose={() => { setShowGalleryModal(false); setEditingImage(null); }}
          onSave={handleAddGalleryItem}
          editingImage={editingImage}
          categories={galleryCategories}
        />
      )}

      <style>{`
        .spinner { animation: spin 0.6s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .category-actions { opacity: 0; transition: opacity 0.2s; }
        div:hover > .category-actions { opacity: 1; }
      `}</style>
    </div>
  );
};

const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none' };
const thStyle = { textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '12px 16px', fontSize: '0.85rem', verticalAlign: 'middle' };

export default EnquiryManager;